import { useState, useRef, useEffect, useCallback } from "react";

export const useTimer = ({
  initialSeconds,
  interval = 1000,
  timerTemplate = (time) => time,
  timerTemplateDone = () => "Done.",
  timerTemplateError = () => "Error.",
  extraTime = 0,
  shouldAddExtraTime = () => false,
  extraTimeLoopCounter = 1,
}) => {
  //#region //! States
  const [timerDone, setTimerDone] = useState(false);
  const [loopCounter, setLoopCounter] = useState(extraTimeLoopCounter);
  const timerIdRef = useRef(null);
  const secondsRef = useRef(initialSeconds);
  const timerComponentRef = useRef(null);
  //#endregion

  //#region //! Timer updates handlers
  const setComponentContent = (template) => {
    if (!timerComponentRef.current) return;
    timerComponentRef.current.textContent = template(secondsRef.current);
  };
  const resetTimer = useCallback(() => {
    setTimerDone(false);
    setLoopCounter(extraTimeLoopCounter);
    secondsRef.current = initialSeconds;
  }, [extraTimeLoopCounter, initialSeconds]);

  const updateTimerDone = useCallback(() => {
    clearInterval(timerIdRef.current);
    setTimerDone(true);

    if (shouldAddExtraTime()) {
      setComponentContent(timerTemplateError);
      return;
    }
    setComponentContent(timerTemplateDone);
  }, [shouldAddExtraTime, timerTemplateDone, timerTemplateError]);

  const updateTimerCount = useCallback(
    (time) => {
      secondsRef.current += time;
      setComponentContent(timerTemplate);
    },
    [timerTemplate]
  );

  const addExtraTime = useCallback(() => {
    if (loopCounter > 0) {
      secondsRef.current += extraTime;
      updateTimerCount(0);
      setLoopCounter((prevState) => prevState - 1);
      return;
    }

    setTimerDone(true);
    setComponentContent(timerTemplateError);
  }, [extraTime, loopCounter, timerTemplateError, updateTimerCount]);
  //#endregion

  //#region //! Timer
  useEffect(() => {
    if (!timerComponentRef.current) {
      resetTimer();
      return;
    }

    timerIdRef.current = setInterval(() => {
      if (secondsRef.current > 0) updateTimerCount(-1);
      else if (shouldAddExtraTime()) addExtraTime();
      else updateTimerDone();
    }, interval);

    if (timerDone) updateTimerDone();

    return () => clearInterval(timerIdRef.current);
  }, [
    addExtraTime,
    interval,
    loopCounter,
    resetTimer,
    shouldAddExtraTime,
    timerDone,
    updateTimerCount,
    updateTimerDone,
  ]);
  //#endregion

  return {
    timerComponentRef,
    timerDone,
    setTimerDone,
    resetTimer,
  };
};
